import React, { JSX, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  LockReset as LockResetIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { validatePasswordStrength } from 'domain/auth-rules';
import { useAuth } from 'hooks/AuthContext';
import { BackstoryPageProps } from './BackstoryTab';

export const ResetPasswordPage = (_props: BackstoryPageProps): JSX.Element => {
  const { confirmPasswordReset, isLoading } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<'form' | 'success' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const resetToken = new URLSearchParams(window.location.search).get('token');
    if (!resetToken) {
      setStatus('error');
      setErrorMessage('Invalid or missing reset link. Please request a new one.');
    } else {
      setToken(resetToken);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setErrorMessage(null);
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    if (!token) {
      setErrorMessage('Missing reset token.');
      return;
    }
    const strength = validatePasswordStrength(newPassword);
    if (!strength.isValid) {
      setErrorMessage(strength.issues.join('. '));
      return;
    }
    const result = await confirmPasswordReset(token, newPassword);
    if (result.ok) {
      setStatus('success');
    } else if (result.tokenInvalid) {
      setStatus('error');
      setErrorMessage('The reset link is invalid or has expired. Please request a new one.');
    } else {
      setErrorMessage(result.error ?? 'Password reset failed. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {status === 'success' && <ResetSuccess onLogin={() => navigate('/login')} />}
          {status === 'error' && (
            <ResetError errorMessage={errorMessage} onLogin={() => navigate('/login')} />
          )}
          {status === 'form' && (
            <ResetForm
              newPassword={newPassword}
              confirmPassword={confirmPassword}
              showPassword={showPassword}
              errorMessage={errorMessage}
              isLoading={isLoading}
              onSubmit={handleSubmit}
              onNewPasswordChange={setNewPassword}
              onConfirmPasswordChange={setConfirmPassword}
              onTogglePassword={() => setShowPassword(v => !v)}
              onLogin={() => navigate('/login')}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

function ResetSuccess(props: { onLogin: () => void }): JSX.Element {
  return (
    <Box textAlign="center">
      <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom color="success.main">
        Password updated!
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your password has been changed. You can now sign in with your new password.
      </Typography>
      <Button variant="contained" fullWidth onClick={props.onLogin}>
        Go to Sign In
      </Button>
    </Box>
  );
}

function ResetError(props: { errorMessage: string | null; onLogin: () => void }): JSX.Element {
  return (
    <Box textAlign="center">
      <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom color="error.main">
        Link invalid or expired
      </Typography>
      {props.errorMessage && (
        <Alert severity="error" sx={{ mt: 1, mb: 3 }}>
          {props.errorMessage}
        </Alert>
      )}
      <Button variant="outlined" fullWidth onClick={props.onLogin} sx={{ mb: 1 }}>
        Back to Sign In
      </Button>
    </Box>
  );
}

function ResetForm(props: {
  newPassword: string;
  confirmPassword: string;
  showPassword: boolean;
  errorMessage: string | null;
  isLoading: boolean;
  onSubmit: (event: React.FormEvent) => void;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onTogglePassword: () => void;
  onLogin: () => void;
}): JSX.Element {
  return (
    <>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <LockResetIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5">Set new password</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose a strong password for your account.
      </Typography>
      <Box component="form" onSubmit={props.onSubmit}>
        <TextField
          fullWidth
          margin="normal"
          label="New password"
          type={props.showPassword ? 'text' : 'password'}
          value={props.newPassword}
          onChange={e => props.onNewPasswordChange(e.target.value)}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={props.onTogglePassword}
                  onMouseDown={e => e.preventDefault()}
                  edge="end"
                >
                  {props.showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Confirm new password"
          type={props.showPassword ? 'text' : 'password'}
          value={props.confirmPassword}
          onChange={e => props.onConfirmPasswordChange(e.target.value)}
          required
          error={props.confirmPassword.length > 0 && props.newPassword !== props.confirmPassword}
          helperText={
            props.confirmPassword.length > 0 && props.newPassword !== props.confirmPassword
              ? 'Passwords do not match'
              : ''
          }
        />
        {props.errorMessage && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {props.errorMessage}
          </Alert>
        )}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={props.isLoading || !props.newPassword || !props.confirmPassword}
          sx={{ mt: 3, mb: 2 }}
        >
          {props.isLoading ? <CircularProgress size={20} /> : 'Update password'}
        </Button>
        <Box textAlign="center">
          <Link component="button" variant="body2" onClick={props.onLogin} underline="hover">
            Back to Sign In
          </Link>
        </Box>
      </Box>
    </>
  );
}
